import React, { useState } from 'react';

import { createCountryIconProps } from '~/UI/CountryIcon/CountryIcon';
import GenericChart, { chartColors, exportConfig, fontFamily, langConfig } from '~/GenericChart/GenericChart';
import Highcharts from 'highcharts';
import { roundedHumanFormat } from '~/utils/Price/Price';

import type { CountryResponse } from 'com.cogvio.pricemonitor.api';
import { ChartModeCheckboxes, ChartMode } from './ChartModeCheckboxes';
import { ERPApiResult } from '~/libs/infrastructure/schemas/erp';
import { formatDateFromString } from '~/libs/utils/format';

const createImageStringElement = (code: string, name: string, size = 16) =>
	code
		? `
		<img ${Object.entries(
			createCountryIconProps({
				code,
				name,
				size,
			})
		)
			.filter(([_, value]) => value)
			.map(([key, value]) => `${key === 'className' ? 'class' : key}="${value}"`)
			.join(' ')}
		>`
		: '';

const isSeriesVisible = (
	countryName: string,
	chartMode: null | string,
	userCountrySelected: null | string[],
	erpImpactedCountries: { [countryCode: string]: CountryResponse }
) => {
	if (userCountrySelected && userCountrySelected.length > 0) {
		return userCountrySelected.indexOf(countryName) > -1;
	}

	switch (chartMode) {
		case ChartMode.showAll:
			return true;
		case ChartMode.hideAll:
			return false;
		case ChartMode.erpImpact:
			return countryName in erpImpactedCountries;
		default:
			return true;
	}
};

interface ChartLineProps {
	result: ERPApiResult;
	virtualBrandName?: string;
	virtualProductPackName: string;
}

export function ChartLine({ result, virtualBrandName, virtualProductPackName }: ChartLineProps) {
	const [chartMode, setChartMode] = useState<null | string>(ChartMode.showAll);
	const [userCountrySelected, setUserCountrySelected] = useState<null | string[]>([]);
	const { byCountry, erpImpactedCountries, byMonth, currency, priceChangesByMonth, sortedDates } = result;

	const handleChartModeChange = (type: string) => {
		setUserCountrySelected([]);
		setChartMode(type);
	};

	return (
		<GenericChart
			options={{
				chart: {
					height: 800,
					spacing: [20, 10, 30, 0],
					backgroundColor: 'transparent',
					style: {
						zIndex: 'none',
					},
				},

				colors: chartColors,
				lang: langConfig,

				title: {
					text: `${virtualBrandName || virtualProductPackName} &#8212 IRP${
						chartMode === ChartMode.erpImpact ? ` impacted only` : ' Linechart'
					}`,
					align: 'left',
					margin: 50,
					x: 18,
					widthAdjust: 0,
					style: {
						color: '#5150a9',
						fontWeight: '700',
						fontSize: '20px',
						letterSpacing: '0.05em',
						fontFamily,
					},
				},

				subtitle: {
					text: `<strong>Effect on the list price in the next 4 years</strong><br> Package: <strong>${virtualProductPackName}</strong>`,
					useHTML: true,
					x: 18,
					align: 'left',
					style: {
						color: '#555',
						fontWeight: '500',
						fontSize: '12px',
						lineHeight: '20px',
						letterSpacing: '0.05em',
						fontFamily,
					},
				},

				series: Object.entries(byCountry)
					.sort(([a], [b]) => a.localeCompare(b))
					.map(([_, dataset]) => ({
						type: 'line',
						name: dataset.name || '',
						visible: isSeriesVisible(dataset.name, chartMode, userCountrySelected, erpImpactedCountries),
						step: 'left',
						data: sortedDates.map((date) => {
							const isCountryPrice = byMonth[date].find((data) => data.country.alpha2 === dataset.alpha2)?.price.value;

							return {
								y: isCountryPrice ? Number(isCountryPrice) : null,
								alpha2: dataset.alpha2,
							};
						}),
						custom: {
							alpha2: dataset.alpha2,
						},
						label: {
							enabled: false,
						},
						dataLabels: {
							enabled: false,
						},
					})),

				xAxis: {
					crosshair: {
						width: 2,
					},
					title: {
						text: 'Month',
						margin: 4,
						style: {
							color: '#333',
							fontWeight: '600',
							fontSize: '12px',
							fontFamily,
						},
					},

					tickInterval: 1,
					tickmarkPlacement: 'on',
					categories: sortedDates.map((date) => formatDateFromString(date, 'short')),
					showLastLabel: true,
					tickWidth: 0,
					tickLength: 0,
					lineWidth: 0,
					gridLineWidth: 1,
					gridLineColor: '#999999',
					gridLineDashStyle: 'Dash',
					labels: {
						align: 'right',
						x: 0,
						rotation: -90,
						style: {
							color: '#666',
							fontWeight: '500',
							fontSize: '11px',
							fontFamily,
						},
					},
					showFirstLabel: true,
				},

				yAxis: [
					{
						crosshair: true,
						title: {
							text: currency ? `Price [${currency}]` : 'Price',
							margin: 10,
							x: 0,
							style: {
								color: '#333',
								fontWeight: '600',
								fontSize: '12px',
								fontFamily,
							},
						},
						tickLength: 0,
						minorTickLength: 0,
						minorGridLineWidth: 0,
						gridLineWidth: 1,
						gridLineDashStyle: 'Dash',
						labels: {
							align: 'right',
							x: 4,
							y: 14,
							style: {
								color: '#666',
								fontWeight: '500',
								fontSize: '11px',
								fontFamily,
							},
						},
						showFirstLabel: false,
					},
					{
						crosshair: true,
						linkedTo: 0,
						gridLineWidth: 0,
						opposite: true,
						title: {
							text: null,
						},
						labels: {
							align: 'left',
							x: 0,
							y: 14,
							style: {
								color: '#666',
								fontWeight: '500',
								fontSize: '11px',
								fontFamily,
							},
						},
						showFirstLabel: false,
					},
				],

				exporting: exportConfig,

				legend: {
					align: 'left',
					verticalAlign: 'top',
					borderWidth: 0,
					x: 12,
					margin: 10,
					itemMarginBottom: 6,
					useHTML: true,
					itemStyle: {
						fontFamily: "'Lucida Grande', 'Lucida Sans Unicode', Arial, Helvetica, sans-serif",
						fontSize: '12px',
						fontWeight: 'bold',
					},
					labelFormatter: function () {
						return `${createImageStringElement(this.options.custom?.alpha2, this.name, 16)}
												${this.name}`;
					},
				},

				tooltip: {
					shared: false,
					borderRadius: 0,
					borderColor: '#000',
					backgroundColor: 'rgba(255,255,255,1)',
					borderWidth: 1,
					shadow: false,
					useHTML: true,
					padding: 0,
					headerFormat: '',

					formatter: function (this: Highcharts.TooltipFormatterContextObject): string {
						if (!this.x) {
							return '';
						}
						const isPriceChangeThisMonth = priceChangesByMonth[formatDateFromString(String(this.x))];

						return `
						<div class="erp-tooltip-wrapper">
							<h2 class="tooltip-h2">${formatDateFromString(String(this.x))}</h2>
							<div>
								<table class="table-timeline-tooltip" cellspacing="0" cellpadding="0" width="100%">
									<tr>
                    					<td>
											<strong style="display:block;color: ${this.point.color};">
												<span class="img">${createImageStringElement(
													this.point.series.userOptions.custom?.alpha2,
													this.point.series.name,
													14
												)}</span>
												${this.point.series.name}
											</strong>
											</td>
                    						<td class="td-right"><strong>${roundedHumanFormat(
																	currency,
																	String(this.point.y || 0)
																)}</strong></td>
                							</tr>
								</table>
								${
									isPriceChangeThisMonth
										? isPriceChangeThisMonth
												.filter((data) => {
													const pointCountryCode = this.point.series.userOptions.custom?.alpha2;
													return data.country.alpha2 === pointCountryCode;
												})
												.map(
													(priceChange) => `
														${priceChange.revisionType ? `<p><strong>Revision type:</strong>&nbsp;${priceChange.revisionType}</p>` : ''}
														<p><strong>Rules:</strong>&nbsp;${priceChange.ruleDescription}</p>
														<p>
															${(priceChange.priceAffectingCountries?.length || 0) > 0 ? '<strong>Price impact:</strong><br>' : ''}
															${(priceChange.priceAffectingCountries || [])
																.map(
																	({ country, price }) =>
																		`<span class="impact-item"><span class="img">${createImageStringElement(
																			country.alpha2,
																			country.name,
																			14
																		)}</span><strong>${roundedHumanFormat(
																			price.currency,
																			String(price.value)
																		)}</strong></span>`
																)
																.join('<span class="impact-item-hr">&nbsp;• </span>')}
														</p>
													`
												)
										: ''
								}
							</div>
						</div>`;
					},
					valueDecimals: 2,
					style: {
						width: 380,
						color: '#000',
						fontSize: '12px',
						zIndex: 1000000000,
						fontFamily,
					},
				},

				plotOptions: {
					series: {
						lineWidth: 2,
						step: 'right',
						marker: {
							symbol: 'circle',
							radius: 4,
						},
						events: {
							legendItemClick: function ({ target }: { target: Highcharts.Series }) {
								setChartMode(null);
								setUserCountrySelected((prev) => {
									const normalizedPrev = new Set(Array.isArray(prev) ? prev : []);
									if (normalizedPrev.has(target.name)) {
										normalizedPrev.delete(target.name);
									} else {
										normalizedPrev.add(target.name);
									}

									return Array.from(normalizedPrev.values());
								});
								return false;
							},
						},
					},
				},
			}}
		>
			<ChartModeCheckboxes chartMode={chartMode} onChartModeSet={handleChartModeChange} />
		</GenericChart>
	);
}
